import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { fetchProducts } from '../features/products/productsSlice';
import Header from './Header';
import { API_URL, BASE_URL, getImageUrl, normalizeList, normalizeStorageUrl } from '../config';
import { isYouTubeUrl, isValidYouTubeUrl, normalizeYouTubeUrl, isClothesOrGarmentsCategory, getClothesSizeOptions } from '../utils/productHelpers';
import './AdminProductsPage.css';

const DESCRIPTION_EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean'],
  ],
};

const boolFlag = (obj, camel) => {
  if (!obj) return false;
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
  const value = obj[camel] ?? obj[pascal];
  return value === true || value === 'true' || value === 1;
};

const DESCRIPTION_EDITOR_FORMATS = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'script',
  'list', 'bullet', 'indent', 'align',
  'blockquote', 'code-block',
  'link', 'image',
];

const createEditLocalImage = (file, preview) => ({
  id: Date.now() + Math.random(),
  file,
  preview,
  uploadedUrl: null,
  uploading: false,
  error: null,
  isExisting: false,
});

const createEditColorVariantRow = () => ({
  id: Date.now() + Math.random(),
  colorId: '',
  sizeIds: [],
  sizeStocks: {},
  stock: '',
  images: [],
});

const getRowSizeStock = (row, sizeId) => {
  const map = row?.sizeStocks || {};
  return map[sizeId] ?? map[String(sizeId)] ?? '';
};

const mapColorVariantPayload = (row) => {
  const sizeIds = (row.sizeIds || []).map((id) => parseInt(id, 10)).filter((id) => id > 0);
  const sizeStocks = sizeIds.map((sizeId) => ({
    sizeId,
    stock: parseInt(getRowSizeStock(row, sizeId), 10) || 0,
  }));
  return {
    colorId: parseInt(row.colorId, 10),
    sizeIds,
    sizeStocks,
    stock: sizeStocks.reduce((sum, item) => sum + item.stock, 0),
    images: row.images
      .filter((img) => img.uploadedUrl)
      .map((img) => ({ imageUrl: img.uploadedUrl })),
  };
};

const AdminProductsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);
  const token = localStorage.getItem('jwtToken');
  const { productId: editRouteProductId } = useParams();
  const isEditPageMode = Boolean(editRouteProductId);

  const [products, setProducts] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editColors, setEditColors] = useState([]);
  const [editSizes, setEditSizes] = useState([]);
  const [editSizeIds, setEditSizeIds] = useState([]);
  const [editSizeDropdownValue, setEditSizeDropdownValue] = useState('');
  const [editImages, setEditImages] = useState([]);
  const [editColorVariants, setEditColorVariants] = useState([]);
  const [editEnableBanner, setEditEnableBanner] = useState(false);
  const [editBannerImage, setEditBannerImage] = useState({ file: null, preview: null, uploadedUrl: null, uploading: false, error: null });
  const [editShowOnHomePageBrand, setEditShowOnHomePageBrand] = useState(false);
  const [editIsTrending, setEditIsTrending] = useState(false);
  const [editIsFeatured, setEditIsFeatured] = useState(false);
  const [editBrandImage, setEditBrandImage] = useState({ file: null, preview: null, uploadedUrl: null, uploading: false, error: null });
  const [editVideoThumbnailImage, setEditVideoThumbnailImage] = useState({
    file: null,
    preview: null,
    uploadedUrl: null,
    uploading: false,
    error: null,
  });
  const [editLegacyVideoUrl, setEditLegacyVideoUrl] = useState(null);
  const [editProductSellers, setEditProductSellers] = useState([]); // current mappings { productSellerId, sellerId, sellerName, sellerPrice, stockQuantity, ... }
  const [editSellersToRemove, setEditSellersToRemove] = useState([]); // productSellerIds to delete on save
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editColorVariantError, setEditColorVariantError] = useState('');
  const fileInputRef = useRef(null);
  const editBannerFileInputRef = useRef(null);
  const editBrandFileInputRef = useRef(null);
  const editVideoThumbFileInputRef = useRef(null);
  const editRequestIdRef = useRef(0);

  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isLoggedIn || userRole !== 'Admin') {
      navigate('/login');
    }
  }, [isLoggedIn, userRole, navigate]);

  useEffect(() => {
    fetchAllProducts();
    fetchSubCategories();
  }, []);

  // If user opens /admin/products/:productId/edit, load the edit data for that product.
  useEffect(() => {
    if (!isEditPageMode) return;
    if (loading) return;
    const id = parseInt(editRouteProductId, 10);
    if (!id) return;
    if (editProduct?.productId === id && editModalOpen) return;

    const found = products.find((p) => Number(p.productId) === id);
    if (found) {
      openEditModal(found);
    }
  }, [isEditPageMode, editRouteProductId, loading, products, editProduct, editModalOpen]);

  const fetchAllProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/Products`);
      const data = response.data;
      const arr = Array.isArray(data) ? data : (data?.$values ?? []);
      setProducts(arr);
    } catch (err) {
      setError('Failed to load products.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/SubCategories`);
      const data = response.data;
      const arr = Array.isArray(data) ? data : (data?.$values ?? []);
      setSubCategories(arr);
    } catch (err) {
      console.error('Failed to load subcategories');
    }
  };

  const filteredProducts = products.filter((p) =>
    p.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.brandName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.subCategory?.subCategoryName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Edit handlers
  const openEditModal = async (product) => {
    const requestId = editRequestIdRef.current + 1;
    editRequestIdRef.current = requestId;

    setEditError('');
    setEditModalOpen(false);
    setEditProduct(null);
    setEditProduct(product);
    setEditColors([]);
    setEditSizes([]);
    setEditSizeIds([]);
    setEditSizeDropdownValue('');
    setEditImages([]);
    setEditColorVariants([]);
    setEditEnableBanner(false);
    setEditBannerImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: null });
    setEditShowOnHomePageBrand(boolFlag(product, 'showOnHomePageBrand'));
    setEditIsTrending(boolFlag(product, 'isTrending'));
    setEditIsFeatured(boolFlag(product, 'isFeatured'));
    setEditBrandImage({
      file: null,
      preview: product.brandImageUrl ? getImageUrl(product.brandImageUrl) : null,
      uploadedUrl: product.brandImageUrl ? getImageUrl(product.brandImageUrl) : null,
      uploading: false,
      error: null,
    });
    setEditVideoThumbnailImage({
      file: null,
      preview: null,
      uploadedUrl: null,
      uploading: false,
      error: null,
    });
    setEditLegacyVideoUrl(null);
    setEditProductSellers([]);
    setEditSellersToRemove([]);
    setEditColorVariantError('');

    setEditForm({
      productName: product.productName || '',
      description: product.description || '',
      price: product.price || 0,
      originalPrice: product.originalPrice ?? '',
      stock: product.stock || 0,
      subCategoryId: product.subCategoryId || '',
      brandName: product.brandName || '',
      shortDescription: product.shortDescription || '',
      youtubeVideoUrl: '',
    });
    setEditSellersToRemove([]);

    const subForClothes = subCategories.find((s) => String(s.subCategoryId) === String(product.subCategoryId)) || product.subCategory;
    const isClothesProduct = isClothesOrGarmentsCategory({
      categoryId: product.categoryId ?? product.subCategory?.categoryId ?? subForClothes?.categoryId,
      categoryName: product.categoryName ?? product.subCategory?.categoryName ?? subForClothes?.categoryName,
    });

    // Fetch product detail so clothes variant images can be edited by color
    try {
      const response = await axios.get(`${API_URL}/Products/detail/${product.productId}`);
      if (editRequestIdRef.current !== requestId) return;

      const images = normalizeList(response.data?.images);
      const variants = normalizeList(response.data?.variants);
      const variantMap = new Map(variants.map((variant) => [variant.variantId ?? variant.VariantId, variant]));

      const sharedImages = [];
      const colorGroups = new Map();

      variants.forEach((variant) => {
        const colorId = variant.colorId ?? variant.ColorId;
        if (!colorId) return;
        const key = String(colorId);
        const stockVal = variant.stock ?? variant.Stock ?? 0;
        if (!colorGroups.has(key)) {
          colorGroups.set(key, {
            id: Date.now() + Math.random() + Number(colorId),
            colorId: key,
            stock: String(stockVal),
            colorName: variant.colorName ?? variant.ColorName,
            sizeIds: [],
            sizeStocks: {},
            images: [],
          });
        }
        const sizeId = Number(variant.sizeId ?? variant.SizeId);
        if (sizeId > 0 && !colorGroups.get(key).sizeIds.includes(sizeId)) {
          colorGroups.get(key).sizeIds.push(sizeId);
        }
        if (sizeId > 0) {
          colorGroups.get(key).sizeStocks = {
            ...colorGroups.get(key).sizeStocks,
            [sizeId]: String(stockVal),
          };
        }
      });

      images.forEach((img) => {
        const mappedImage = {
          id: img.imageId,
          variantId: img.variantId ?? null,
          isMain: Boolean(img.isMain),
          imageUrl: getImageUrl(img.imageUrl),
          preview: getImageUrl(img.imageUrl),
          isExisting: true,
          uploadedUrl: getImageUrl(img.imageUrl),
          error: null,
        };

        if (!isClothesProduct || img.variantId == null) {
          sharedImages.push(mappedImage);
          return;
        }

        const variant = variantMap.get(img.variantId);
        const colorId = variant?.colorId ?? variant?.ColorId;
        if (!colorId) {
          sharedImages.push(mappedImage);
          return;
        }

        const key = String(colorId);
        if (!colorGroups.has(key)) {
          colorGroups.set(key, {
            id: Date.now() + Math.random() + Number(colorId),
            colorId: key,
            stock: String(variant?.stock ?? variant?.Stock ?? 0),
            sizeIds: [],
            sizeStocks: {},
            images: [],
          });
        }
        colorGroups.get(key).images.push(mappedImage);
      });

      setEditImages(sharedImages);
      setEditColorVariants(isClothesProduct ? Array.from(colorGroups.values()) : []);
      const sizeIdsFromVariants = [...new Set(
        variants
          .map((variant) => variant.sizeId ?? variant.SizeId)
          .filter((sizeId) => Number(sizeId) > 0)
          .map((sizeId) => Number(sizeId))
      )];
      setEditSizeIds(isClothesProduct ? sizeIdsFromVariants : []);
      setEditSizeDropdownValue('');
      const rawBannerUrl = response.data?.bannerImageUrl || null;
      const bannerPreviewUrl = rawBannerUrl ? getImageUrl(rawBannerUrl) : null;
      const rawVideoUrl = response.data?.videoUrl || null;
      const rawVideoThumbUrl = response.data?.videoThumbnailUrl || null;
      const isYoutubeVideo = isYouTubeUrl(rawVideoUrl);
      const storedThumbUrl = rawVideoThumbUrl ? normalizeStorageUrl(rawVideoThumbUrl) : null;
      setEditEnableBanner(Boolean(response.data?.enableBanner));
      setEditBannerImage({
        file: null,
        preview: bannerPreviewUrl,
        uploadedUrl: rawBannerUrl,
        uploading: false,
        error: null,
      });
      const brandImageUrl = response.data?.brandImageUrl ? getImageUrl(response.data.brandImageUrl) : null;
      setEditShowOnHomePageBrand(boolFlag(response.data, 'showOnHomePageBrand'));
      setEditIsTrending(boolFlag(response.data, 'isTrending'));
      setEditIsFeatured(boolFlag(response.data, 'isFeatured'));
      setEditBrandImage({
        file: null,
        preview: brandImageUrl,
        uploadedUrl: brandImageUrl,
        uploading: false,
        error: null,
      });
      setEditForm((prev) => ({
        ...prev,
        youtubeVideoUrl: isYoutubeVideo ? rawVideoUrl : '',
      }));
      setEditLegacyVideoUrl(isYoutubeVideo ? null : rawVideoUrl);
      setEditVideoThumbnailImage({
        file: null,
        preview: storedThumbUrl ? getImageUrl(storedThumbUrl) : null,
        uploadedUrl: storedThumbUrl,
        uploading: false,
        error: null,
      });
    } catch (err) {
      setEditImages([]);
      setEditColorVariants([]);
      setEditSizeIds([]);
      setEditEnableBanner(false);
      setEditBannerImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: null });
      setEditShowOnHomePageBrand(boolFlag(product, 'showOnHomePageBrand'));
      setEditIsTrending(boolFlag(product, 'isTrending'));
      setEditIsFeatured(boolFlag(product, 'isFeatured'));
      setEditBrandImage({
        file: null,
        preview: product.brandImageUrl ? getImageUrl(product.brandImageUrl) : null,
        uploadedUrl: product.brandImageUrl ? getImageUrl(product.brandImageUrl) : null,
        uploading: false,
        error: null,
      });
      setEditLegacyVideoUrl(null);
      setEditVideoThumbnailImage({
        file: null,
        preview: null,
        uploadedUrl: null,
        uploading: false,
        error: null,
      });
    }

    if (isClothesProduct) {
      try {
        const [colorsRes, sizesRes] = await Promise.all([
          axios.get(`${API_URL}/Colors`),
          axios.get(`${API_URL}/Sizes`),
        ]);
        if (editRequestIdRef.current !== requestId) return;
        setEditColors(normalizeList(colorsRes.data));
        setEditSizes(normalizeList(sizesRes.data));
      } catch (err) {
        setEditColors([]);
        setEditSizes([]);
      }
    } else {
      setEditColors([]);
      setEditSizes([]);
      setEditSizeIds([]);
    }

    // Fetch current product-seller mappings
    try {
      const psRes = await axios.get(`${API_URL}/ProductSellers/product/${product.productId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { all: true },
      });
      if (editRequestIdRef.current !== requestId) return;
      const mappings = normalizeList(psRes.data).map((ps) => ({
        ...ps,
        productSellerId: ps.productSellerId ?? ps.ProductSellerId,
        sellerName: ps.sellerName ?? ps.SellerName,
        sellerPrice: ps.sellerPrice ?? ps.SellerPrice,
        stockQuantity: ps.stockQuantity ?? ps.StockQuantity,
        deliveryDays: ps.deliveryDays ?? ps.DeliveryDays ?? 5,
        deliveryCharge: Number(ps.deliveryCharge ?? ps.DeliveryCharge ?? 0),
        sellerAddress: ps.sellerAddress ?? ps.SellerAddress ?? '',
        isActive: ps.isActive !== false && ps.IsActive !== false,
      }));
      setEditProductSellers(mappings);
    } catch (err) {
      setEditProductSellers([]);
    }

    if (editRequestIdRef.current !== requestId) return;
    setEditModalOpen(true);
  };

  const closeEditModal = (redirectPathOrEvent = '/') => {
    const redirectPath =
      typeof redirectPathOrEvent === 'string' && redirectPathOrEvent.trim()
        ? redirectPathOrEvent
        : '/';
    setEditModalOpen(false);
    setEditProduct(null);
    setEditForm({});
    setEditColors([]);
    setEditSizes([]);
    setEditSizeIds([]);
    setEditSizeDropdownValue('');
    setEditImages([]);
    setEditColorVariants([]);
    setEditEnableBanner(false);
    setEditBannerImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: null });
    setEditShowOnHomePageBrand(false);
    setEditBrandImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: null });
    setEditVideoThumbnailImage({
      file: null,
      preview: null,
      uploadedUrl: null,
      uploading: false,
      error: null,
    });
    setEditLegacyVideoUrl(null);
    setEditProductSellers([]);
    setEditSellersToRemove([]);
    setEditError('');
    setEditColorVariantError('');
    if (isEditPageMode) {
      navigate(redirectPath);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFilesSelect = (e) => {
    const files = Array.from(e.target.files || []);
    addEditFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addEditFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    files.forEach((file) => {
      if (!allowedTypes.includes(file.type) || file.size > maxSize) return;

      const reader = new FileReader();
      reader.onloadend = () => {
        setEditImages((prev) => [...prev, createEditLocalImage(file, reader.result)]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleEditDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      addEditFiles(files);
    }
  };

  const handleEditDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const uploadEditImage = async (imageId) => {
    const image = editImages.find((img) => img.id === imageId);
    if (!image || !image.file || image.uploadedUrl) return;

    setEditImages((prev) =>
      prev.map((img) => img.id === imageId ? { ...img, uploading: true } : img)
    );

    const formData = new FormData();
    formData.append('file', image.file);

    try {
      const response = await axios.post(`${API_URL}/ImageUpload/product`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      const uploadedUrl = `${BASE_URL}${response.data.imageUrl}`;
      setEditImages((prev) =>
        prev.map((img) => img.id === imageId ? { ...img, uploadedUrl, uploading: false } : img)
      );
    } catch (err) {
      setEditImages((prev) =>
        prev.map((img) => img.id === imageId ? { ...img, uploading: false, error: 'Upload failed' } : img)
      );
    }
  };

  const uploadAllEditImages = async () => {
    const toUpload = editImages.filter((img) => img.file && !img.uploadedUrl && !img.uploading);
    for (const img of toUpload) {
      await uploadEditImage(img.id);
    }
  };

  const removeEditImage = (imageId) => {
    setEditImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const setEditBannerFromFile = (file) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      setEditBannerImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: 'Please select JPG, PNG, GIF, or WebP image.' });
      return;
    }
    if (file.size > maxSize) {
      setEditBannerImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: 'Banner image must be under 5MB.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setEditBannerImage({ file, preview: reader.result, uploadedUrl: null, uploading: false, error: null });
    };
    reader.readAsDataURL(file);
  };

  const handleEditBannerDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    setEditBannerFromFile(file);
  };

  const handleEditBannerDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const uploadEditBannerImage = async () => {
    if (!editEnableBanner) return null;
    if (!editBannerImage.file && !editBannerImage.uploadedUrl) {
      setEditError('Please choose and upload a banner image (or turn off banner).');
      return false;
    }
    if (!editBannerImage.file || editBannerImage.uploadedUrl) return editBannerImage.uploadedUrl;

    setEditBannerImage((prev) => ({ ...prev, uploading: true, error: null }));
    const formData = new FormData();
    formData.append('file', editBannerImage.file);

    try {
      const response = await axios.post(`${API_URL}/ImageUpload/product`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      const uploadedUrl = normalizeStorageUrl(response.data.imageUrl);
      setEditBannerImage((prev) => ({
        ...prev,
        uploadedUrl,
        preview: getImageUrl(uploadedUrl),
        uploading: false,
      }));
      return uploadedUrl;
    } catch (err) {
      const errorMsg = err.response?.data || 'Banner upload failed.';
      setEditBannerImage((prev) => ({
        ...prev,
        uploading: false,
        error: typeof errorMsg === 'string' ? errorMsg : 'Banner upload failed.',
      }));
      return false;
    }
  };

  const setEditBrandImageFromFile = (file) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      setEditBrandImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: 'Please select JPG, PNG, GIF, or WebP image.' });
      return;
    }
    if (file.size > maxSize) {
      setEditBrandImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: 'Brand image must be under 5MB.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setEditBrandImage({ file, preview: reader.result, uploadedUrl: null, uploading: false, error: null });
    };
    reader.readAsDataURL(file);
  };

  const uploadEditBrandImage = async () => {
    if (!editBrandImage.file && !editBrandImage.uploadedUrl) return null;
    if (!editBrandImage.file || editBrandImage.uploadedUrl) return editBrandImage.uploadedUrl;

    setEditBrandImage((prev) => ({ ...prev, uploading: true, error: null }));
    const formData = new FormData();
    formData.append('file', editBrandImage.file);

    try {
      const response = await axios.post(`${API_URL}/ImageUpload/product`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      const uploadedUrl = `${BASE_URL}${response.data.imageUrl}`;
      setEditBrandImage((prev) => ({ ...prev, uploadedUrl, uploading: false }));
      return uploadedUrl;
    } catch (err) {
      const errorMsg = err.response?.data || 'Brand image upload failed.';
      setEditBrandImage((prev) => ({
        ...prev,
        uploading: false,
        error: typeof errorMsg === 'string' ? errorMsg : 'Brand image upload failed.',
      }));
      return false;
    }
  };

  const setEditVideoThumbnailFromFile = (file) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      setEditVideoThumbnailImage({
        file: null,
        preview: null,
        uploadedUrl: null,
        uploading: false,
        error: 'Please select a JPG, PNG, WebP, or GIF image.',
      });
      return;
    }
    if (file.size > maxSize) {
      setEditVideoThumbnailImage({
        file: null,
        preview: null,
        uploadedUrl: null,
        uploading: false,
        error: 'Image size must be under 5MB.',
      });
      return;
    }
    const preview = URL.createObjectURL(file);
    setEditVideoThumbnailImage((prev) => {
      if (prev.preview && prev.preview.startsWith('blob:')) {
        URL.revokeObjectURL(prev.preview);
      }
      return {
        file,
        preview,
        uploadedUrl: null,
        uploading: false,
        error: null,
      };
    });
  };

  const handleEditVideoThumbnailDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    setEditVideoThumbnailFromFile(file);
  };

  const handleEditVideoThumbnailDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const uploadEditVideoThumbnail = async () => {
    if (!editVideoThumbnailImage.file && !editVideoThumbnailImage.uploadedUrl) return true;
    if (!editVideoThumbnailImage.file || editVideoThumbnailImage.uploadedUrl) return true;

    setEditVideoThumbnailImage((prev) => ({ ...prev, uploading: true, error: null }));
    const formData = new FormData();
    formData.append('file', editVideoThumbnailImage.file);
    try {
      const response = await axios.post(`${API_URL}/ImageUpload/product`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      const uploadedUrl = normalizeStorageUrl(response.data.imageUrl);
      setEditVideoThumbnailImage((prev) => ({
        ...prev,
        uploadedUrl,
        preview: getImageUrl(uploadedUrl),
        uploading: false,
      }));
      return true;
    } catch (err) {
      const errorMsg = err.response?.data || 'Video image upload failed.';
      setEditVideoThumbnailImage((prev) => ({
        ...prev,
        uploading: false,
        error: typeof errorMsg === 'string' ? errorMsg : 'Video image upload failed.',
      }));
      return false;
    }
  };

  const clearEditProductVideo = () => {
    setEditForm((prev) => ({ ...prev, youtubeVideoUrl: '' }));
    setEditLegacyVideoUrl(null);
    setEditVideoThumbnailImage((prev) => {
      if (prev.preview && prev.preview.startsWith('blob:')) {
        URL.revokeObjectURL(prev.preview);
      }
      return {
        file: null,
        preview: null,
        uploadedUrl: null,
        uploading: false,
        error: null,
      };
    });
  };

  const updateEditColorVariantRow = (rowId, updater) => {
    setEditColorVariants((prev) => prev.map((row) => (
      row.id === rowId ? updater(row) : row
    )));
  };

  const addEditColorVariantRow = () => {
    setEditColorVariants((prev) => [...prev, createEditColorVariantRow()]);
    setEditColorVariantError('');
  };

  const removeEditColorVariantRow = (rowId) => {
    setEditColorVariants((prev) => prev.filter((row) => row.id !== rowId));
    setEditColorVariantError('');
  };

  const handleEditColorVariantColorChange = (rowId, colorId) => {
    updateEditColorVariantRow(rowId, (row) => ({ ...row, colorId }));
    setEditColorVariantError('');
  };

  const addSizeToEditColorVariant = (rowId, sizeId) => {
    if (!sizeId) return;
    updateEditColorVariantRow(rowId, (row) => {
      const current = Array.isArray(row.sizeIds) ? row.sizeIds : [];
      if (current.some((id) => Number(id) === Number(sizeId))) return row;
      return {
        ...row,
        sizeIds: [...current, sizeId],
        sizeStocks: { ...(row.sizeStocks || {}), [sizeId]: getRowSizeStock(row, sizeId) },
      };
    });
    setEditError('');
  };

  const removeSizeFromEditColorVariant = (rowId, sizeId) => {
    updateEditColorVariantRow(rowId, (row) => {
      const nextStocks = { ...(row.sizeStocks || {}) };
      delete nextStocks[sizeId];
      delete nextStocks[String(sizeId)];
      return {
        ...row,
        sizeIds: (row.sizeIds || []).filter((id) => Number(id) !== Number(sizeId)),
        sizeStocks: nextStocks,
      };
    });
  };

  const updateEditColorSizeStock = (rowId, sizeId, stock) => {
    updateEditColorVariantRow(rowId, (row) => ({
      ...row,
      sizeStocks: { ...(row.sizeStocks || {}), [sizeId]: stock },
    }));
  };

  const addFilesToEditColorVariant = (rowId, files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    const errors = [];

    files.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type.`);
        return;
      }
      if (file.size > maxSize) {
        errors.push(`${file.name}: File too large (max 5MB).`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        updateEditColorVariantRow(rowId, (row) => ({
          ...row,
          images: [...row.images, createEditLocalImage(file, reader.result)],
        }));
      };
      reader.readAsDataURL(file);
    });

    setEditColorVariantError(errors.join(' '));
  };

  const handleEditColorVariantDrop = (rowId, e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      addFilesToEditColorVariant(rowId, files);
    }
  };

  const handleEditColorVariantDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const uploadEditColorVariantImage = async (rowId, imageId) => {
    const row = editColorVariants.find((variantRow) => variantRow.id === rowId);
    const image = row?.images.find((img) => img.id === imageId);
    if (!image || !image.file || image.uploadedUrl) return;

    updateEditColorVariantRow(rowId, (variantRow) => ({
      ...variantRow,
      images: variantRow.images.map((img) => (
        img.id === imageId ? { ...img, uploading: true, error: null } : img
      )),
    }));

    const formData = new FormData();
    formData.append('file', image.file);

    try {
      const response = await axios.post(`${API_URL}/ImageUpload/product`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      const uploadedUrl = `${BASE_URL}${response.data.imageUrl}`;
      updateEditColorVariantRow(rowId, (variantRow) => ({
        ...variantRow,
        images: variantRow.images.map((img) => (
          img.id === imageId ? { ...img, uploadedUrl, uploading: false } : img
        )),
      }));
    } catch (err) {
      updateEditColorVariantRow(rowId, (variantRow) => ({
        ...variantRow,
        images: variantRow.images.map((img) => (
          img.id === imageId ? { ...img, uploading: false, error: 'Upload failed.' } : img
        )),
      }));
    }
  };

  const uploadAllEditColorVariantImages = async (rowId) => {
    const row = editColorVariants.find((variantRow) => variantRow.id === rowId);
    const pendingImages = row?.images.filter((img) => img.file && !img.uploadedUrl && !img.uploading);
    if (!pendingImages?.length) return;

    for (const img of pendingImages) {
      await uploadEditColorVariantImage(rowId, img.id);
    }
  };

  const removeEditColorVariantImage = (rowId, imageId) => {
    updateEditColorVariantRow(rowId, (row) => ({
      ...row,
      images: row.images.filter((img) => img.id !== imageId),
    }));
  };

  // Seller mapping in edit modal
  const editProductSellersVisible = editProductSellers.filter((ps) => !editSellersToRemove.includes(ps.productSellerId));
  const updateEditProductSeller = (productSellerId, field, value) => {
    setEditProductSellers((prev) =>
      prev.map((ps) => (ps.productSellerId === productSellerId ? { ...ps, [field]: value } : ps))
    );
  };
  const markEditSellerRemoved = (productSellerId) => {
    setEditSellersToRemove((prev) => (prev.includes(productSellerId) ? prev : [...prev, productSellerId]));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError('');

    const productName = editForm.productName?.trim();
    const subCategoryId = editForm.subCategoryId ? parseInt(editForm.subCategoryId, 10) : 0;
    const price = parseFloat(editForm.price);
    const originalPrice = editForm.originalPrice !== '' && editForm.originalPrice != null ? parseFloat(editForm.originalPrice) : null;
    const stock = parseInt(editForm.stock, 10);

    if (!productName) {
      setEditError('Product name is required.');
      return;
    }
    if ((editShowOnHomePageBrand || editBrandImage.file || editBrandImage.uploadedUrl) && !editForm.brandName?.trim()) {
      setEditError('Please enter a brand name before saving brand image details.');
      return;
    }
    if (!subCategoryId) {
      setEditError('Please select a subcategory.');
      return;
    }
    const submitSubCategory = subCategories.find((s) => String(s.subCategoryId) === String(editForm.subCategoryId))
      || editProduct?.subCategory;
    const isEditClothesProduct = isClothesOrGarmentsCategory({
      categoryId: editProduct?.categoryId ?? editProduct?.subCategory?.categoryId ?? submitSubCategory?.categoryId,
      categoryName: editProduct?.categoryName ?? editProduct?.subCategory?.categoryName ?? submitSubCategory?.categoryName,
    });
    if (Number.isNaN(price) || price < 0) {
      setEditError('Please enter a valid price.');
      return;
    }
    if (!isEditClothesProduct && (Number.isNaN(stock) || stock < 0)) {
      setEditError('Please enter a valid stock value.');
      return;
    }

    // Seller price must match product price (Amazon-style: selected seller amount = product price)
    const productPrice = price;
    const sellerPriceMismatch = (sp) => {
      const n = Number(sp);
      return Number.isNaN(n) || Math.abs(n - productPrice) > 0.01;
    };
    for (const ps of editProductSellersVisible) {
      if (sellerPriceMismatch(ps.sellerPrice)) {
        setEditError(`Seller price must match product price (₹${productPrice.toFixed(2)}). Please correct "${ps.sellerName}" and other seller prices.`);
        return;
      }
    }
    // Check for unuploaded images
    const unuploaded = editImages.filter((img) => img.file && !img.uploadedUrl && !img.error);
    if (unuploaded.length > 0) {
      setEditError('Please upload all images before saving.');
      return;
    }

    if (isEditClothesProduct) {
      if (!editColorVariants.length) {
        setEditError('Please add at least one color variant for clothes products.');
        return;
      }

      if (editColorVariants.some((row) => !row.colorId)) {
        setEditError('Please select a color for each color variant.');
        return;
      }

      if (editClothesSizes.sizeOptions.length > 0 && editColorVariants.some((row) => !(row.sizeIds || []).length)) {
        setEditError('Please select at least one size for each color.');
        return;
      }

      const uniqueColorIds = new Set();
      for (const row of editColorVariants) {
        if (uniqueColorIds.has(row.colorId)) {
          setEditError('Each color variant should be added only once.');
          return;
        }
        uniqueColorIds.add(row.colorId);
      }

      if (editColorVariants.some((row) => !row.images.some((img) => img.uploadedUrl))) {
        setEditError('Please upload at least one image for every color variant.');
        return;
      }

      const invalidSizeStock = editColorVariants.find((row) =>
        (row.sizeIds || []).some((sizeId) => {
          const qty = parseInt(getRowSizeStock(row, sizeId), 10);
          return Number.isNaN(qty) || qty < 0;
        })
      );
      if (invalidSizeStock) {
        setEditError('Please enter stock for each size of every color.');
        return;
      }
    }

    const unuploadedColorVariantImages = editColorVariants.flatMap((row) =>
      row.images.filter((img) => img.file && !img.uploadedUrl && !img.error)
    );
    if (unuploadedColorVariantImages.length > 0) {
      setEditError('Please upload all color variant images before saving.');
      return;
    }
    if (editEnableBanner && editBannerImage.file && !editBannerImage.uploadedUrl) {
      setEditError('Please upload banner image before saving.');
      return;
    }
    const youtubeUrl = editForm.youtubeVideoUrl?.trim() || '';
    const hasYoutubeUrl = youtubeUrl.length > 0;
    const hasVideoThumbnail = Boolean(editVideoThumbnailImage.uploadedUrl || editVideoThumbnailImage.file);
    const hasLegacyVideo = Boolean(editLegacyVideoUrl);

    if (hasYoutubeUrl && !isValidYouTubeUrl(youtubeUrl)) {
      setEditError('Please enter a valid YouTube video URL.');
      return;
    }
    if (hasYoutubeUrl && !editVideoThumbnailImage.uploadedUrl) {
      setEditError('Please upload a video thumbnail image before saving.');
      return;
    }
    if (!hasYoutubeUrl && !hasLegacyVideo && hasVideoThumbnail) {
      setEditError('Please enter the YouTube video URL for the uploaded video image.');
      return;
    }
    if (editVideoThumbnailImage.file && !editVideoThumbnailImage.uploadedUrl) {
      setEditError('Please upload the video thumbnail image before saving.');
      return;
    }
    if (editBrandImage.file && !editBrandImage.uploadedUrl) {
      setEditError('Please upload the brand image before saving.');
      return;
    }
    if (editShowOnHomePageBrand && !editBrandImage.uploadedUrl) {
      setEditError('Please upload a brand image before enabling it for the home page.');
      return;
    }

    const uploadedImages = editImages
      .filter((img) => img.uploadedUrl)
      .map((img) => ({ imageUrl: img.uploadedUrl }));
    const uploadedColorVariants = editColorVariants
      .filter((row) => row.colorId)
      .map(mapColorVariantPayload)
      .filter((row) => row.images.length > 0);
    const allSizeIds = [...new Set(uploadedColorVariants.flatMap((row) => row.sizeIds || []))];
    const resolvedStock = isEditClothesProduct
      ? uploadedColorVariants.reduce((sum, row) => sum + (Number(row.stock) || 0), 0)
      : stock;

    const resolvedBannerUrl = await uploadEditBannerImage();
    if (resolvedBannerUrl === false) {
      return;
    }
    const thumbnailUploaded = await uploadEditVideoThumbnail();
    if (thumbnailUploaded === false) {
      return;
    }
    const resolvedBrandImageUrl = await uploadEditBrandImage();
    if (resolvedBrandImageUrl === false) {
      return;
    }

    let resolvedVideoUrl = null;
    let resolvedVideoThumbnailUrl = null;
    if (hasYoutubeUrl) {
      resolvedVideoUrl = normalizeYouTubeUrl(youtubeUrl);
      resolvedVideoThumbnailUrl = editVideoThumbnailImage.uploadedUrl
        ? normalizeStorageUrl(editVideoThumbnailImage.uploadedUrl)
        : null;
    } else if (hasLegacyVideo) {
      resolvedVideoUrl = editLegacyVideoUrl;
      resolvedVideoThumbnailUrl = editVideoThumbnailImage.uploadedUrl
        ? normalizeStorageUrl(editVideoThumbnailImage.uploadedUrl)
        : null;
    }

    const authToken = localStorage.getItem('jwtToken');
    if (!authToken) {
      setEditError('Your session expired. Please log in again, then save.');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/Products/${editProduct.productId}`,
        {
          productName,
          description: editForm.description?.trim() || null,
          price,
          originalPrice: originalPrice,
          imageUrl: uploadedImages[0]?.imageUrl || uploadedColorVariants[0]?.images[0]?.imageUrl || editProduct.imageUrl,
          stock: resolvedStock,
          subCategoryId,
          brandName: editForm.brandName?.trim() || null,
          brandImageUrl: resolvedBrandImageUrl || null,
          showOnHomePageBrand: editShowOnHomePageBrand,
          isTrending: editIsTrending,
          isFeatured: editIsFeatured,
          shortDescription: editForm.shortDescription?.trim() || null,
          videoUrl: resolvedVideoUrl,
          videoThumbnailUrl: resolvedVideoThumbnailUrl,
          images: uploadedImages,
          colorIds: isEditClothesProduct ? uploadedColorVariants.map((row) => row.colorId) : undefined,
          colorVariants: isEditClothesProduct ? uploadedColorVariants : undefined,
          sizeIds: isEditClothesProduct && allSizeIds.length ? allSizeIds : undefined,
          enableBanner: editEnableBanner,
          bannerImageUrl: editEnableBanner
            ? (resolvedBannerUrl ? normalizeStorageUrl(resolvedBannerUrl) : null)
            : null,
        },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` } }
      );

      const auth = { headers: { Authorization: `Bearer ${authToken}` } };

      // Remove product-seller mappings
      for (const productSellerId of editSellersToRemove) {
        try {
          await axios.delete(`${API_URL}/ProductSellers/${productSellerId}`, auth);
        } catch (err) {
          console.warn('Failed to remove seller mapping', productSellerId, err);
        }
      }

      for (const ps of editProductSellersVisible) {
        await axios.put(
          `${API_URL}/ProductSellers/${ps.productSellerId}`,
          {
            sellerPrice: price,
            deliveryDays: Number(ps.deliveryDays) > 0 ? Number(ps.deliveryDays) : 5,
            deliveryCharge: Number(ps.deliveryCharge) || 0,
            sellerAddress: ps.sellerAddress ?? '',
            stockQuantity: Number(ps.stockQuantity) || 0,
            isActive: ps.isActive !== false,
          },
          { ...auth, headers: { ...auth.headers, 'Content-Type': 'application/json' } }
        );
      }

      closeEditModal('/');
      fetchAllProducts();
      dispatch(fetchProducts(null));
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setEditError('Your login session is not valid for this API. Log out, log in again as Admin, then save.');
        return;
      }
      const data = err.response?.data;
      const message =
        (typeof data === 'string' && data) ||
        data?.message ||
        (typeof data === 'object' && data ? JSON.stringify(data) : null) ||
        err.message ||
        'Failed to update product.';
      setEditError(message);
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const openDeleteModal = (product) => {
    setProductToDelete(product);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setProductToDelete(null);
  };

  const handleDelete = async () => {
    if (!productToDelete) return;

    setDeleting(true);
    setError('');
    try {
      await axios.delete(`${API_URL}/Products/${productToDelete.productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      closeDeleteModal();
      setError('');
      fetchAllProducts();
      dispatch(fetchProducts(null));
    } catch (err) {
      const msg = err.response?.data?.message ?? err.response?.data;
      setError(typeof msg === 'string' ? msg : 'Failed to delete product.');
    } finally {
      setDeleting(false);
    }
  };

  const hasUnuploadedImages = editImages.some((img) => img.file && !img.uploadedUrl && !img.uploading && !img.error);
  const isUploading = editImages.some((img) => img.uploading);
  const editSubCategory = subCategories.find((s) => String(s.subCategoryId) === String(editForm.subCategoryId))
    || editProduct?.subCategory;
  const editIsClothesProduct = isClothesOrGarmentsCategory({
    categoryId: editProduct?.categoryId ?? editProduct?.subCategory?.categoryId ?? editSubCategory?.categoryId,
    categoryName: editProduct?.categoryName ?? editProduct?.subCategory?.categoryName ?? editSubCategory?.categoryName,
  });
  const editClothesSizes = getClothesSizeOptions(editSizes, editSubCategory?.subCategoryName);
  const selectedEditSizes = editSizeIds
    .map((sizeId) => editSizes.find((size) => Number(size.sizeId) === Number(sizeId)))
    .filter(Boolean);
  const isEditColorVariantUploading = editColorVariants.some((row) => row.images.some((img) => img.uploading));

  const handleEditSizeDropdownChange = (e) => {
    const sizeId = parseInt(e.target.value, 10);
    setEditSizeDropdownValue('');
    if (!sizeId) return;
    setEditSizeIds((prev) => (prev.includes(sizeId) ? prev : [...prev, sizeId]));
    setEditError('');
  };

  const removeEditSize = (sizeId) => {
    setEditSizeIds((prev) => prev.filter((id) => Number(id) !== Number(sizeId)));
  };

  if (!isLoggedIn || userRole !== 'Admin') {
    return null;
  }

  return (
    <div className={`admin-products-page ${isEditPageMode ? 'admin-edit-fullscreen' : ''}`}>
      <Header />

      <div className="admin-container">
        <div className="admin-toolbar">
          <h1>Product Management</h1>
          <div className="toolbar-actions">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {isEditPageMode && (!editModalOpen || !editProduct) && (
          <div className="loading">Loading product...</div>
        )}

        {!isEditPageMode && (loading ? (
          <div className="loading">Loading products...</div>
        ) : (
          <div className="products-grid">
            {filteredProducts.length === 0 ? (
              <div className="no-products">No products found.</div>
            ) : (
              filteredProducts.map((product) => (
                <div key={product.productId} className="product-card">
                  <div className="product-image">
                    {product.imageUrl ? (
                      <img src={getImageUrl(product.imageUrl)} alt={product.productName} />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </div>
                  <div className="product-info">
                    <h3 className="product-name">{product.productName}</h3>
                    {(boolFlag(product, 'isFeatured') || boolFlag(product, 'isTrending')) && (
                      <div className="product-home-tags">
                        {boolFlag(product, 'isFeatured') && <span className="product-home-tag featured">Featured</span>}
                        {boolFlag(product, 'isTrending') && <span className="product-home-tag trending">Trending</span>}
                      </div>
                    )}
                    <p className="product-brand">{product.brandName || 'No brand'}</p>
                    <p className="product-category">
                      {product.subCategory?.category?.categoryName} › {product.subCategory?.subCategoryName}
                    </p>
                    <div className="product-details">
                      <div className="product-price-block">
                        {product.originalPrice != null && Number(product.originalPrice) > Number(product.price) && Number(product.originalPrice) > 0 && (
                          <>
                            <span className="product-original-price">₹{Number(product.originalPrice).toFixed(2)}</span>
                            <span className="product-discount-badge">
                              {Math.round((1 - Number(product.price) / Number(product.originalPrice)) * 100)}% off
                            </span>
                          </>
                        )}
                        <span className="product-price">₹{product.price != null ? Number(product.price).toFixed(2) : 'N/A'}</span>
                      </div>
                      <span className="product-stock">Stock: {product.stock}</span>
                    </div>
                  </div>
                  <div className="product-actions">
                    <button
                      className="edit-btn"
                      onClick={() => navigate(`/admin/products/${product.productId}/edit`)}
                    >
                      Edit
                    </button>
                    <button className="delete-btn" onClick={() => openDeleteModal(product)}>Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )) }
      </div>

      {/* Edit Modal */}
      {editModalOpen && editProduct && (
        <div
          className={`modal-overlay ${isEditPageMode ? 'admin-edit-page-mode' : ''}`}
          onClick={isEditPageMode ? undefined : closeEditModal}
          key={`edit-modal-${editProduct.productId}`}
        >
          <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                Edit Product {editProduct.productName ? `- ${editProduct.productName}` : ''}
              </h2>
              <button className="close-btn" onClick={closeEditModal}>×</button>
            </div>
            <form onSubmit={handleEditSubmit} className="edit-form" noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input
                    name="productName"
                    value={editForm.productName}
                    onChange={handleEditFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>SubCategory *</label>
                  <select
                    name="subCategoryId"
                    value={editForm.subCategoryId}
                    onChange={handleEditFormChange}
                    required
                  >
                    <option value="">Select subcategory</option>
                    {subCategories.map((s) => (
                      <option key={s.subCategoryId} value={s.subCategoryId}>
                        {s.categoryName} › {s.subCategoryName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group description-editor-group">
                <label>Description</label>
                <ReactQuill
                  theme="snow"
                  value={editForm.description || ''}
                  onChange={(value) => setEditForm((prev) => ({ ...prev, description: value }))}
                  placeholder="Product description (rich text)"
                  modules={DESCRIPTION_EDITOR_MODULES}
                  formats={DESCRIPTION_EDITOR_FORMATS}
                  className="description-rich-editor"
                />
              </div>

              <div className="form-group">
                <label>Short Description</label>
                <input
                  name="shortDescription"
                  value={editForm.shortDescription}
                  onChange={handleEditFormChange}
                />
              </div>

              <div className="form-row pricing-fields">
                <div className="form-group">
                  <label htmlFor="edit-price">Price *</label>
                  <input
                    id="edit-price"
                    name="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.price}
                    onChange={handleEditFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-mrp">MRP (optional)</label>
                  <input
                    id="edit-mrp"
                    name="originalPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Leave empty if no discount"
                    value={editForm.originalPrice}
                    onChange={handleEditFormChange}
                  />
                </div>
                {!editIsClothesProduct && (
                <div className="form-group">
                  <label htmlFor="edit-stock">Stock *</label>
                  <input
                    id="edit-stock"
                    name="stock"
                    type="number"
                    min="0"
                    value={editForm.stock}
                    onChange={handleEditFormChange}
                    required
                  />
                </div>
                )}
              </div>

              <div className="form-group">
                <label>Brand Name</label>
                <input
                  name="brandName"
                  value={editForm.brandName}
                  onChange={handleEditFormChange}
                />
              </div>

              <div className="form-group">
                <label>Home page product tags</label>
                <p className="field-hint">Tagged products appear in the matching home page section. You can select both.</p>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editIsFeatured}
                    onChange={(e) => setEditIsFeatured(e.target.checked)}
                  />
                  Featured product
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editIsTrending}
                    onChange={(e) => setEditIsTrending(e.target.checked)}
                  />
                  Trending product
                </label>
              </div>

              <div className="form-group image-upload-section">
                <label>Brand Image</label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editShowOnHomePageBrand}
                    onChange={(e) => setEditShowOnHomePageBrand(e.target.checked)}
                  />
                  Show this brand in home page "Our Brands" section
                </label>

                <div className="banner-upload-box">
                  {editBrandImage.preview && (
                    <div className="brand-image-preview">
                      <img src={editBrandImage.preview} alt="Brand preview" />
                    </div>
                  )}

                  {editBrandImage.uploadedUrl && (
                    <div className="upload-success">✓ Brand image ready</div>
                  )}

                  {editBrandImage.error && (
                    <div className="upload-error">{editBrandImage.error}</div>
                  )}

                  <div className="upload-actions">
                    <input
                      ref={editBrandFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        setEditBrandImageFromFile(file);
                        e.target.value = '';
                      }}
                    />

                    <button
                      type="button"
                      className="upload-btn"
                      onClick={() => editBrandFileInputRef.current?.click()}
                      disabled={editBrandImage.uploading || saving}
                    >
                      Choose brand image
                    </button>

                    <button
                      type="button"
                      className="upload-btn"
                      onClick={uploadEditBrandImage}
                      disabled={editBrandImage.uploading || !editBrandImage.file || !!editBrandImage.uploadedUrl || saving}
                    >
                      {editBrandImage.uploading ? 'Uploading...' : 'Upload brand image'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-group image-upload-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editEnableBanner}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEditEnableBanner(checked);
                      if (!checked) {
                        setEditBannerImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: null });
                        if (editBannerFileInputRef.current) editBannerFileInputRef.current.value = '';
                      }
                    }}
                  />
                  Show this product in banner slider (upload banner image)
                </label>

                {editEnableBanner && (
                  <div className="banner-upload-box">
                    {(editBannerImage.preview || editBannerImage.uploadedUrl) && (
                      <div className="banner-preview">
                        <img
                          src={editBannerImage.preview || getImageUrl(editBannerImage.uploadedUrl)}
                          alt="Banner preview"
                        />
                      </div>
                    )}

                    {editBannerImage.uploadedUrl && (
                      <div className="upload-success">✓ Banner ready</div>
                    )}

                    {editBannerImage.error && (
                      <div className="upload-error">{editBannerImage.error}</div>
                    )}

                    <div
                      className="banner-drop-zone image-drop-zone multi"
                      onDrop={handleEditBannerDrop}
                      onDragOver={handleEditBannerDragOver}
                    >
                      <div className="drop-zone-content">
                        <span className="drop-icon">🖼️</span>
                        <span>Drag &amp; drop banner image here, or use choose button</span>
                        <span className="file-hint">JPG, PNG, GIF, WebP (max 5MB)</span>
                      </div>
                    </div>

                    <div className="upload-actions">
                      <input
                        ref={editBannerFileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          setEditBannerFromFile(file);
                          e.target.value = '';
                        }}
                      />

                      <button
                        type="button"
                        className="upload-btn"
                        onClick={() => editBannerFileInputRef.current?.click()}
                        disabled={editBannerImage.uploading || saving}
                      >
                        Choose banner image
                      </button>

                      <button
                        type="button"
                        className="upload-btn"
                        onClick={uploadEditBannerImage}
                        disabled={editBannerImage.uploading || !editBannerImage.file || !!editBannerImage.uploadedUrl || saving}
                      >
                        {editBannerImage.uploading ? 'Uploading...' : 'Upload banner'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group image-upload-section">
                <label htmlFor="editYoutubeVideoUrl">Product Video — YouTube (optional)</label>
                <p className="field-hint">
                  Upload a thumbnail image and link a YouTube URL. Customers will see the image on the home page and open the video on YouTube when they click it.
                </p>
                {editLegacyVideoUrl && (
                  <p className="field-hint">
                    This product has an uploaded video file. Add a YouTube URL below to replace it, or remove the video to clear it.
                  </p>
                )}
                <input
                  id="editYoutubeVideoUrl"
                  name="youtubeVideoUrl"
                  type="url"
                  className="video-youtube-url-input"
                  value={editForm.youtubeVideoUrl || ''}
                  onChange={handleEditFormChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                  disabled={saving}
                />
                {(editVideoThumbnailImage.preview || editVideoThumbnailImage.uploadedUrl) && (
                  <div className="video-preview video-thumbnail-preview">
                    <img
                      src={editVideoThumbnailImage.preview || getImageUrl(editVideoThumbnailImage.uploadedUrl)}
                      alt="Video thumbnail preview"
                    />
                  </div>
                )}
                {editVideoThumbnailImage.uploadedUrl && (
                  <div className="upload-success">✓ Video thumbnail uploaded</div>
                )}
                {editVideoThumbnailImage.error && (
                  <div className="upload-error">{editVideoThumbnailImage.error}</div>
                )}
                <div
                  className="image-drop-zone multi"
                  onClick={() => editVideoThumbFileInputRef.current?.click()}
                  onDrop={handleEditVideoThumbnailDrop}
                  onDragOver={handleEditVideoThumbnailDragOver}
                >
                  <div className="drop-zone-content">
                    <span className="drop-icon">🖼️</span>
                    <span>Drag & drop video thumbnail here, or click to select</span>
                    <span className="file-hint">JPG, PNG, WebP, GIF (max 5MB)</span>
                  </div>
                  <input
                    ref={editVideoThumbFileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    className="file-input-hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setEditVideoThumbnailFromFile(file);
                      e.target.value = '';
                    }}
                  />
                </div>
                <div className="upload-actions">
                  <button
                    type="button"
                    className="upload-btn"
                    onClick={uploadEditVideoThumbnail}
                    disabled={
                      editVideoThumbnailImage.uploading
                      || !editVideoThumbnailImage.file
                      || !!editVideoThumbnailImage.uploadedUrl
                      || saving
                    }
                  >
                    {editVideoThumbnailImage.uploading ? 'Uploading...' : 'Upload video image'}
                  </button>
                  {(editForm.youtubeVideoUrl || editLegacyVideoUrl || editVideoThumbnailImage.preview || editVideoThumbnailImage.uploadedUrl) && (
                    <button
                      type="button"
                      className="upload-btn"
                      onClick={clearEditProductVideo}
                      disabled={saving}
                    >
                      Remove video
                    </button>
                  )}
                </div>
              </div>

              {/* Seller mapping */}
              <div className="form-group seller-mapping-edit">
                <label>Seller mapping</label>
                <p className="form-hint">Update which sellers offer this product and their price/stock. Remove or add sellers below.</p>
                <p className="form-hint mrp-hint">Seller price must match product price (₹{(Number(editForm.price) || 0).toFixed(2)}), so the amount charged matches the listed price.</p>
                {editProductSellersVisible.length > 0 && (
                  <div className="edit-sellers-list">
                    {editProductSellersVisible.map((ps) => (
                      <div key={ps.productSellerId} className="edit-seller-row">
                        <div className="edit-seller-field">
                          <span className="edit-seller-field-label">Seller</span>
                          <span className="seller-name">{ps.sellerName}</span>
                        </div>
                        <div className="edit-seller-field">
                          <label className="edit-seller-field-label" htmlFor={`edit-seller-price-${ps.productSellerId}`}>Price (₹)</label>
                          <input
                            id={`edit-seller-price-${ps.productSellerId}`}
                            type="number"
                            step="0.01"
                            min="0"
                            value={ps.sellerPrice ?? ''}
                            onChange={(e) => updateEditProductSeller(ps.productSellerId, 'sellerPrice', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                        <div className="edit-seller-field">
                          <label className="edit-seller-field-label" htmlFor={`edit-seller-stock-${ps.productSellerId}`}>Stock</label>
                          <input
                            id={`edit-seller-stock-${ps.productSellerId}`}
                            type="number"
                            min="0"
                            value={ps.stockQuantity ?? ''}
                            onChange={(e) => updateEditProductSeller(ps.productSellerId, 'stockQuantity', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                        <div className="edit-seller-field">
                          <label className="edit-seller-field-label" htmlFor={`edit-seller-delivery-${ps.productSellerId}`}>Delivery</label>
                          <select
                            id={`edit-seller-delivery-${ps.productSellerId}`}
                            value={Number(ps.deliveryCharge) > 0 ? 'paid' : 'free'}
                            onChange={(e) => updateEditProductSeller(
                              ps.productSellerId,
                              'deliveryCharge',
                              e.target.value === 'free' ? 0 : (Number(ps.deliveryCharge) > 0 ? Number(ps.deliveryCharge) : 40)
                            )}
                            disabled={saving}
                          >
                            <option value="free">Free delivery</option>
                            <option value="paid">Charges applied</option>
                          </select>
                        </div>
                        <div className="edit-seller-field">
                          <label className="edit-seller-field-label" htmlFor={`edit-seller-charge-${ps.productSellerId}`}>Charge (₹)</label>
                          <input
                            id={`edit-seller-charge-${ps.productSellerId}`}
                            type="number"
                            step="0.01"
                            min="0"
                            value={ps.deliveryCharge ?? 0}
                            onChange={(e) => {
                              const next = e.target.value === '' ? 0 : Number(e.target.value);
                              updateEditProductSeller(ps.productSellerId, 'deliveryCharge', Number.isNaN(next) ? 0 : next);
                            }}
                            disabled={saving}
                          />
                        </div>
                        <div className="edit-seller-field col-action">
                          <span className="edit-seller-field-label">&nbsp;</span>
                          <button
                            type="button"
                            className="remove-seller-btn"
                            onClick={() => markEditSellerRemoved(ps.productSellerId)}
                            disabled={saving}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {editSellersToRemove.length > 0 && (
                  <p className="form-hint removed-hint">{editSellersToRemove.length} seller(s) will be removed on save.</p>
                )}
              </div>

              {/* Images */}
              <div className="form-group">
                <label>{editIsClothesProduct ? 'Sizes, colors & product images' : 'Product Images'}</label>
                {editIsClothesProduct ? (
                  <>
                    <p className="form-hint">
                      On each color row pick the colour, the sizes it is sold in, stock for each size, and photos. Archive unused sizes/colors under Admin → Sizes &amp; Colors.
                    </p>
                    {editImages.length > 0 && (
                      <div className="edit-color-image-group">
                        <div className="edit-color-image-group-title">Shared images</div>
                        <div className="edit-images-grid">
                          {editImages.map((img, index) => (
                            <div key={img.id} className={`edit-image-item ${img.isMain || index === 0 ? 'main' : ''}`}>
                              <img src={img.preview || img.imageUrl} alt={`Shared ${index + 1}`} />
                              {(img.isMain || index === 0) && <span className="main-badge">Main</span>}
                              {img.uploading && <div className="uploading-overlay">Uploading...</div>}
                              {img.uploadedUrl && !img.isExisting && <div className="uploaded-check">✓</div>}
                              <button
                                type="button"
                                className="remove-img-btn"
                                onClick={() => removeEditImage(img.id)}
                                disabled={img.uploading}
                              >×</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div
                      className="add-images-zone image-drop-zone multi"
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={handleEditDrop}
                      onDragOver={handleEditDragOver}
                    >
                      <div className="drop-zone-content">
                        <span className="drop-icon">📷</span>
                        <span>Drag &amp; drop shared images here, or click to select</span>
                        <span className="file-hint">JPG, PNG, GIF, WebP (max 5MB each)</span>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleEditFilesSelect}
                        className="file-input-hidden"
                      />
                    </div>
                    {hasUnuploadedImages && (
                      <button type="button" className="upload-btn" onClick={uploadAllEditImages} disabled={isUploading}>
                        {isUploading ? 'Uploading...' : 'Upload Shared Images'}
                      </button>
                    )}
                    {editColorVariantError && <div className="edit-error">{editColorVariantError}</div>}
                    <div className="edit-color-variant-builder">
                      {editColorVariants.map((variantRow) => {
                        const pendingUploads = variantRow.images.filter((img) => img.file && !img.uploadedUrl && !img.error).length;
                        return (
                          <div key={variantRow.id} className="edit-color-variant-card">
                            <div className="edit-color-variant-header">
                              <select
                                value={variantRow.colorId}
                                onChange={(e) => handleEditColorVariantColorChange(variantRow.id, e.target.value)}
                                className="edit-color-variant-select"
                              >
                                <option value="">Select color</option>
                                {[
                                  ...editColors,
                                  ...(variantRow.colorId && !editColors.some((color) => String(color.colorId) === String(variantRow.colorId))
                                    ? [{ colorId: variantRow.colorId, colorName: variantRow.colorName || 'Archived color' }]
                                    : []),
                                ]
                                  .filter((color) => !editColorVariants.some((row) => row.id !== variantRow.id && String(row.colorId) === String(color.colorId)))
                                  .map((color) => (
                                    <option key={color.colorId} value={color.colorId}>
                                      {color.colorName}
                                    </option>
                                  ))}
                              </select>
                              <label className="color-stock-label">
                                Size
                                <select
                                  value=""
                                  onChange={(e) => {
                                    const sizeId = parseInt(e.target.value, 10);
                                    if (sizeId) addSizeToEditColorVariant(variantRow.id, sizeId);
                                  }}
                                  className="color-size-select"
                                >
                                  <option value="">Select size</option>
                                  {editClothesSizes.sizeOptions.map((size) => (
                                    <option
                                      key={size.sizeId}
                                      value={size.sizeId}
                                      disabled={(variantRow.sizeIds || []).some((id) => Number(id) === Number(size.sizeId))}
                                    >
                                      {size.sizeName}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <button
                                type="button"
                                className="remove-color-variant-btn"
                                onClick={() => removeEditColorVariantRow(variantRow.id)}
                              >
                                Remove color
                              </button>
                            </div>
                            <div className="selected-size-chips">
                              {(variantRow.sizeIds || []).length === 0 ? (
                                <span className="selected-size-empty">No sizes selected for this color yet.</span>
                              ) : (
                                (variantRow.sizeIds || []).map((sizeId) => {
                                  const size = editClothesSizes.sizeOptions.find((s) => Number(s.sizeId) === Number(sizeId))
                                    || editSizes.find((s) => Number(s.sizeId) === Number(sizeId));
                                  if (!size) return null;
                                  return (
                                    <div key={size.sizeId} className="size-stock-item">
                                      <span className="size-stock-name">{size.sizeName}</span>
                                      <input
                                        type="number"
                                        min="0"
                                        className="size-stock-input"
                                        placeholder="Qty"
                                        value={getRowSizeStock(variantRow, size.sizeId)}
                                        onChange={(e) => updateEditColorSizeStock(variantRow.id, size.sizeId, e.target.value)}
                                      />
                                      <button
                                        type="button"
                                        className="size-stock-remove"
                                        onClick={() => removeSizeFromEditColorVariant(variantRow.id, size.sizeId)}
                                        title="Remove size"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  );
                                })
                              )}
                            </div>

                            {variantRow.images.length > 0 && (
                              <div className="color-variant-images-grid">
                                {variantRow.images.map((img, index) => (
                                  <div key={img.id} className="color-variant-image-item">
                                    <img src={img.preview || img.imageUrl} alt={`Colour ${index + 1}`} />
                                    {(img.isMain || index === 0) && <span className="main-badge">Main</span>}
                                    {img.uploading && <div className="image-overlay"><span className="uploading-indicator">Uploading...</span></div>}
                                    {img.uploadedUrl && !img.uploading && <div className="image-uploaded-check">✓</div>}
                                    {img.error && <div className="image-error-badge" title={img.error}>!</div>}
                                    <button
                                      type="button"
                                      className="remove-image-btn"
                                      onClick={() => removeEditColorVariantImage(variantRow.id, img.id)}
                                      disabled={img.uploading}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            <label
                              className="color-variant-upload-box image-drop-zone multi"
                              onDrop={(e) => handleEditColorVariantDrop(variantRow.id, e)}
                              onDragOver={handleEditColorVariantDragOver}
                            >
                              <div className="drop-zone-content">
                                <span className="drop-icon">📷</span>
                                <span>Drag &amp; drop images here, or click to select</span>
                                <span className="file-hint">JPG, PNG, GIF, WebP (max 5MB each, multiple images allowed)</span>
                              </div>
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                multiple
                                className="file-input-hidden"
                                onChange={(e) => {
                                  const files = Array.from(e.target.files || []);
                                  if (files.length) {
                                    addFilesToEditColorVariant(variantRow.id, files);
                                  }
                                  e.target.value = '';
                                }}
                              />
                            </label>

                            {pendingUploads > 0 && (
                              <div className="upload-actions">
                                <button
                                  type="button"
                                  className="upload-btn"
                                  onClick={() => uploadAllEditColorVariantImages(variantRow.id)}
                                  disabled={isEditColorVariantUploading}
                                >
                                  {isEditColorVariantUploading ? 'Uploading...' : `Upload ${pendingUploads} image(s)`}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        className="add-color-variant-btn"
                        onClick={addEditColorVariantRow}
                      >
                        + Add color option
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                {editImages.length > 0 && (
                  <div className="edit-images-grid">
                    {editImages.map((img, index) => (
                      <div key={img.id} className={`edit-image-item ${index === 0 ? 'main' : ''}`}>
                        <img src={img.preview || img.imageUrl} alt={`Product ${index + 1}`} />
                        {index === 0 && <span className="main-badge">Main</span>}
                        {img.uploading && <div className="uploading-overlay">Uploading...</div>}
                        {img.uploadedUrl && !img.isExisting && <div className="uploaded-check">✓</div>}
                        <button
                          type="button"
                          className="remove-img-btn"
                          onClick={() => removeEditImage(img.id)}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="add-images-zone" onClick={() => fileInputRef.current?.click()}>
                  <span>+ Add Images</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleEditFilesSelect}
                    style={{ display: 'none' }}
                  />
                </div>
                {hasUnuploadedImages && (
                  <button type="button" className="upload-btn" onClick={uploadAllEditImages} disabled={isUploading}>
                    {isUploading ? 'Uploading...' : 'Upload New Images'}
                  </button>
                )}
                  </>
                )}
              </div>

              {editError && <div className="edit-error">{editError}</div>}

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={closeEditModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={saving || isUploading || hasUnuploadedImages}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && productToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Product</h2>
              <button className="close-btn" onClick={closeDeleteModal}>×</button>
            </div>
            <div className="delete-content">
              <p>Are you sure you want to delete this product?</p>
              <div className="delete-product-info">
                <strong>{productToDelete.productName}</strong>
                <span>₹{productToDelete.price?.toFixed(2)}</span>
              </div>
              <p className="warning">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </button>
              <button className="confirm-delete-btn" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsPage;
